import { isBefore } from "date-fns";
import { toGlobalId } from "graphql-relay";
import ms from "ms";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import {
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/utils/cn";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import type { pollQuery } from "./__generated__/pollQuery.graphql";

function PollOption({
	id,
	pollId,
	disabled,
	userHasVote,
	isOver,
	winnerOption,
	totalVotes,
	votes,
	userVoted,
	option,
}: {
	id: string;
	pollId: string;
	disabled: boolean;
	userHasVote: boolean;
	isOver: boolean;
	winnerOption: string;
	totalVotes: number;
	votes: number;
	userVoted: boolean;
	option: string;
}) {
	const { track } = useAnalytics();
	const [vote, isLoading] = useMutation(graphql`
    mutation pollVoteCreateMutation($option: String!) {
      registerPollVote(option: $option) {
        id
        options {
          id
          votes
          option
        }
        userPollVotes {
          option {
            option
          }
        }
      }
    }
  `);

	return (
		<button
			type="button"
			className={cn(
				"relative flex flex-row items-center justify-between gap-4 overflow-hidden rounded-md border p-2 transition-all enabled:hover:bg-accent",
				isLoading && "opacity-60",
			)}
			disabled={disabled}
			onClick={() => {
				track(AnalyticsEvents.POLL_VOTED, { poll_id: pollId });
				vote({
					variables: {
						option: id,
					},
				});
			}}
		>
			{(userHasVote || isOver) && totalVotes > 0 ? (
				<div
					className={cn(
						"absolute top-0 left-0 z-0 h-full w-full bg-accent",
						isOver ? "bg-accent" : null,
						isOver && winnerOption === id ? "bg-primary" : null,
					)}
					style={{
						maxWidth: totalVotes <= 0 ? 0 : `${(votes / totalVotes) * 100}%`,
					}}
				/>
			) : null}

			<div className="z-1 flex flex-1 flex-row items-center justify-center gap-4 overflow-hidden">
				<input
					type="radio"
					disabled={disabled}
					checked={userVoted}
					className="size-3 border text-primary checked:fill-primary"
				/>

				<span
					className={cn(
						"line-clamp-1 flex w-full flex-1 text-left font-medium text-foreground text-sm leading-none",
						!isOver && userVoted && "text-accent-foreground",
						isOver ? "font-medium" : null,
						isOver && winnerOption === id && totalVotes > 0
							? "font-semibold text-primary-foreground dark:text-foreground"
							: null,
					)}
				>
					{option}
				</span>
			</div>

			<span
				className={cn(
					"z-1 h-fit font-medium text-foreground text-sm",
					!isOver && userVoted && "text-accent-foreground",
					isOver ? "font-medium" : null,
					isOver && winnerOption === id && totalVotes > 0
						? "font-semibold text-primary-foreground"
						: null,
				)}
			>
				{`${totalVotes > 0 ? ((votes / totalVotes) * 100).toFixed(2) : 0} %`}{" "}
				{isLoading && <Spinner />}
			</span>
		</button>
	);
}

export function Poll({
	id,
	editable,
	onDelete,
}: {
	id: string;
	editable?: boolean;
	onDelete: () => void;
}) {
	const data = useLazyLoadQuery<pollQuery>(
		graphql`
    query pollQuery($id: ID!) {
      poll(id: $id) {
        id
        rawID
        dueDate
        title
        voteType
        voteVisibility
        options {
          id
          votes
          option
        }
        userPollVotes {
          option {
            option
          }
        }
      }
    }
  `,
		{ id: toGlobalId("Poll", id) },
	);

	if (!data.poll) {
		return null;
	}

	const days = Math.floor(
		(new Date(data.poll.dueDate).getTime() - Date.now()) / ms("1d"),
	);

	const userHasVote = data.poll.userPollVotes.length > 0;
	const isOver = isBefore(new Date(data.poll.dueDate), new Date());
	const disabled =
		editable || isOver || (userHasVote && data.poll.voteType === "SINGLE");

	const totalVotes = data.poll.options.reduce(
		(acc, option) => acc + option.votes,
		0,
	);

	const winnerOption = [...data.poll.options].sort(
		(a, b) => b.votes - a.votes,
	)[0]?.id;

	return (
		<div className="flex w-full flex-col items-center">
			<div className="flex w-full max-w-[500px] py-6 flex-col gap-0 rounded-md border">
				<CardHeader className="flex flex-row items-center justify-between pb-6">
					<CardTitle>{data.poll.title}</CardTitle>

					{editable && (
						<div>
							<Button
								variant="ghost"
								size="icon"
								type="button"
								onClick={() => {
									if (
										confirm(
											"Tem certeza que deseja excluir a enquete? Essa ação não poderá ser desfeita!",
										)
									) {
										onDelete();
									}
								}}
							>
								<Icon name="Trash2" />
							</Button>
						</div>
					)}
				</CardHeader>

				<CardContent className="w-full flex flex-col gap-3">
					{data.poll.options.map((option) => (
						<PollOption
							key={option.id}
							id={option.id}
							pollId={data.poll.id}
							disabled={disabled}
							userHasVote={userHasVote}
							isOver={isOver}
							winnerOption={winnerOption}
							totalVotes={totalVotes}
							votes={option.votes}
							userVoted={data.poll.userPollVotes.some(
								(vote) => vote.option.option === option.id,
							)}
							option={option.option}
						/>
					))}
				</CardContent>

				<CardFooter className="mt-6 flex flex-row items-center justify-between">
					<span className="text-muted-foreground text-sm">{`${totalVotes} votos`}</span>

					{isOver ? (
						<span className="font-medium text-destructive text-sm">
							Enquete encerrada
						</span>
					) : (
						<span className="text-muted-foreground text-sm">{`${days} ${
							days === 1 ? "dia restante" : "dias restantes"
						}`}</span>
					)}
				</CardFooter>
			</div>
		</div>
	);
}
